import { useMemo } from 'react';
import type { AllHTMLAttributes, AriaAttributes } from 'react';
import { useToggle } from '../../../hooks';

export type AccordionTargetProps = Pick<AllHTMLAttributes<HTMLDivElement> & AriaAttributes, 'type' | 'aria-expanded' | 'aria-controls'> & {
  onClick?: () => void;
};

export type AccordionBodyProps = Pick<AllHTMLAttributes<HTMLDivElement> & AriaAttributes, 'role' | 'aria-labelledby'>;

type UseAccordionProps = {
  id: string;
  isInitiallyOpen?: boolean;
};

type UseAccordionReturn = {
  isOpen: boolean;
  targetProps: AccordionTargetProps,
  bodyProps: AccordionBodyProps;
};

/**
 * Hook for managing a single accordion state and applying the proper props
 * for both the target and body. Use this if you can't use the default
 * styling provided by the main Accordion component.
 *
 * Note that this includes no styling for the items, however you can add
 * your own styling or even import the styles from Accordion.module.less and build off them.
 * You should also include your own BS `Collapse` component or use <Accordion.Body>.
 *
 * @example
 * const MyCustomAccordion = () => {
 *   const { isOpen, targetProps, bodyProps } = useAccordion({ id: 'MyCustomAccordion' });
 *   return (
 *     <div>
 *       <div {...targetProps}>
 *         {isOpen ? '⬆️ Close' : '⬇️ Open'}
 *       </div>
 *       {isOpen && (
 *         <div {...bodyProps}>
 *           Here is some body content!
 *         </div>
 *       )}
 *     </div>
 *   );
 * }
 */
const useAccordion = ({ id, isInitiallyOpen = true }: UseAccordionProps): UseAccordionReturn => {
  const [isOpen, onToggle] = useToggle(isInitiallyOpen);

  const targetProps: AccordionTargetProps = useMemo(() => ({
    //  This element behaves like a button, so it should have this property.
    type: 'button',
    onClick: onToggle,
    'aria-controls': id,
    'aria-expanded': isOpen,
    tabIndex: 0,
  }), [isOpen, id, onToggle]);

  const bodyProps: AccordionBodyProps = useMemo(() => ({
    role: 'region',
    'aria-labelledby': id,
  }), [id]);

  return {
    isOpen,
    targetProps,
    bodyProps,
  };
};

export default useAccordion;
